import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'

interface Booking {
    _id: string;
    type: string;
    name: string;
    phone: string;
    email: string;
    date?: string;
    time?: string;
    guests?: number;
    message?: string;
    status: string;
    createdAt: string;
    property?: {
        _id: string;
        title: string;
        price: number;
        address: string;
    };
    project?: {
        _id: string;
        name: string;
        location: string;
        address: string;
        city: string;
    };
    purpose?: string;
    propertyType?: string;
    propertySubType?: string;
    interestedHomeLoan?: boolean;
    agreeContact?: boolean;
}

interface AdminInquiriesProps {
    filterType?: 'Project Inquiry' | 'Locality Inquiry' | 'Common Inquiry' | 'All';
}

export default function AdminInquiries({ filterType = 'All' }: AdminInquiriesProps) {
    const { token } = useAuth()
    const [bookings, setBookings] = useState<Booking[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('All')
    const [timeFilter, setTimeFilter] = useState('All')
    const [viewBooking, setViewBooking] = useState<Booking | null>(null)
    const [deleteId, setDeleteId] = useState<string | null>(null)

    useEffect(() => {
        loadBookings()
    }, [token, filterType])

    const loadBookings = () => {
        if (!token) { setLoading(false); return }
        api.adminGetBookings(token)
            .then(data => { 
                if (Array.isArray(data)) {
                    let filteredData = data.filter(b => b.type && b.type.toLowerCase().includes('inquiry'));
                    
                    if (filterType !== 'All') {
                        filteredData = filteredData.filter(b => b.type === filterType);
                    }
                    
                    setBookings(filteredData);
                } 
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }

    const handleStatusChange = async (id: string, status: string) => {
        if (!token) return
        await api.adminUpdateBooking(token, id, { status })
        setBookings(prev => prev.map(b => b._id === id ? { ...b, status } : b))
    }

    const handleDelete = async () => {
        if (!token || !deleteId) return
        await api.adminDeleteBooking(token, deleteId)
        setBookings(prev => prev.filter(b => b._id !== deleteId))
        setDeleteId(null)
    }

    const getPropertyName = (b: Booking) => {
        if (b.project?.name) return b.project.name
        if (b.property?.title) return b.property.title
        return 'N/A'
    }

    const getPropertyLocation = (b: Booking) => {
        if (b.project?.location) return b.project.location
        if (b.property?.address) return b.property.address
        return ''
    }

    const filtered = bookings.filter(b => {
        const matchSearch = b.name.toLowerCase().includes(search.toLowerCase()) ||
            b.phone.includes(search) ||
            getPropertyName(b).toLowerCase().includes(search.toLowerCase()) ||
            (b.propertyType && b.propertyType.toLowerCase().includes(search.toLowerCase()))
        
        const matchStatus = statusFilter === 'All' || b.status === statusFilter

        let matchTime = true
        if (timeFilter !== 'All') {
            const bDate = new Date(b.createdAt)
            const now = new Date()
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            
            if (timeFilter === 'Today') {
                matchTime = bDate >= today
            } else if (timeFilter === 'Yesterday') {
                const yesterday = new Date(today)
                yesterday.setDate(yesterday.getDate() - 1)
                matchTime = bDate >= yesterday && bDate < today
            } else if (timeFilter === 'Last 7 Days') {
                const last7 = new Date(today)
                last7.setDate(last7.getDate() - 7)
                matchTime = bDate >= last7
            } else if (timeFilter === 'Last 30 Days') {
                const last30 = new Date(today)
                last30.setDate(last30.getDate() - 30)
                matchTime = bDate >= last30
            } else if (timeFilter === 'This Month') {
                const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
                matchTime = bDate >= firstOfMonth
            }
        }

        return matchSearch && matchStatus && matchTime
    })

    const counts = {
        all: bookings.length,
        pending: bookings.filter(b => b.status === 'Pending').length,
        completed: bookings.filter(b => b.status === 'Completed').length,
        cancelled: bookings.filter(b => b.status === 'Cancelled').length,
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-black mb-2">
                        {filterType === 'Project Inquiry' ? 'Project Inquiries' : 
                         filterType === 'Locality Inquiry' ? 'Locality Inquiries' : 
                         filterType === 'Common Inquiry' ? 'Common Inquiries' : 'General Inquiries'}
                    </h1>
                    <p className="text-black text-[14px] md:text-[15px] font-bold">
                        {filterType === 'Project Inquiry' ? 'Manage inquiries related to specific projects.' : 
                         filterType === 'Locality Inquiry' ? 'Manage inquiries related to locations and areas.' : 
                         filterType === 'Common Inquiry' ? 'Manage general inquiries from the contact form.' : 'Manage property interest and general client queries from discovery forms.'}
                    </p>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                {[
                    { label: 'Total Volume', count: counts.all, icon: 'fa-envelope-open-text', color: 'bg-[#05144b]', filter: 'All' },
                    { label: 'Unprocessed', count: counts.pending, icon: 'fa-bolt', color: 'bg-amber-500', filter: 'Pending' },
                    { label: 'Successfully Converted', count: counts.completed, icon: 'fa-circle-check', color: 'bg-emerald-500', filter: 'Completed' },
                    { label: 'Archived / Closed', count: counts.cancelled, icon: 'fa-folder-closed', color: 'bg-slate-500', filter: 'Cancelled' },
                ].map((s, i) => (
                    <button 
                        key={i}
                        onClick={() => setStatusFilter(s.filter)}
                        className={`relative overflow-hidden group p-6 rounded-md border transition-all text-left ${
                            statusFilter === s.filter 
                            ? 'bg-white dark:bg-slate-900 border-[#05144b] ring-2 ring-[#05144b]/10 shadow-lg' 
                            : 'bg-white dark:bg-slate-900 border-gray-100 hover:border-[#05144b]/30'
                        }`}
                    >
                        <div className={`w-12 h-12 rounded-md ${s.color} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform shadow-xl`}>
                            <i className={`fa-solid ${s.icon} text-lg`}></i>
                        </div>
                        <p className="text-[13px] font-bold text-black mb-1">{s.label}</p>
                        <h4 className="text-3xl font-bold text-black leading-none tabular-nums">{s.count}</h4>
                    </button>
                ))}
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden mb-10">
                <div className="p-8 border-b border-gray-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6 bg-white/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-md bg-white border border-[#05144b] text-[#05144b] flex items-center justify-center shadow-lg">
                            <i className="fa-solid fa-headset"></i>
                        </div>
                        <div>
                            <h3 className="text-xl md:text-2xl font-bold text-black dark:text-white leading-tight">Inquiry Stream</h3>
                            <p className="text-[13px] md:text-[14px] font-bold text-black">{filtered.length} entries matching criteria</p>
                        </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
                        <div className="relative group w-full md:w-[200px]">
                            <select
                                 className="w-full bg-white dark:bg-slate-800 border border-gray-100 rounded-md py-4 pl-6 pr-12 text-[13px] font-bold text-black focus:ring-2 focus:ring-[#05144b] shadow-inner appearance-none cursor-pointer transition-all"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="All">All Statuses</option>
                                <option value="Pending">Pending</option>
                                <option value="Completed">Completed</option>
                                <option value="Cancelled">Cancelled</option>
                            </select>
                        </div>

                        <div className="relative group w-full md:w-[400px]">
                            <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                                <i className="fa-solid fa-magnifying-glass text-black/20 group-focus-within:text-black transition-colors"></i>
                            </div>
                            <input 
                                type="text" 
                                 className="w-full bg-white dark:bg-slate-800 border border-gray-100 rounded-md py-4 pl-14 pr-6 text-[15px] font-bold text-black focus:ring-2 focus:ring-[#05144b] shadow-inner placeholder:text-gray-400 transition-all"
                                placeholder="Search by name, phone, type or project..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-32 text-center">
                            <div className="w-16 h-16 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                            <p className="text-black font-bold text-[14px]">Parsing Inquiry Database...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-32 text-center">
                            <div className="w-24 h-24 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-8 border border-gray-100">
                                <i className="fa-solid fa-envelope-open text-4xl text-black/10"></i>
                            </div>
                            <h3 className="text-xl font-bold text-black dark:text-white mb-2 uppercase">No Inquiries Found</h3>
                            <p className="text-black/40 text-sm max-w-xs mx-auto font-bold">General inquiry forms will appear here once users submit project or locality interest.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/50 dark:bg-slate-800/30 border-b border-gray-100">
                                    <th className="px-8 py-5 text-[12px] font-bold text-black uppercase">Client</th>
                                    <th className="px-8 py-5 text-[12px] font-bold text-[#05144b] uppercase">Customer Hub</th>
                                    <th className="px-8 py-5 text-[12px] font-bold text-[#05144b] uppercase">Inquiry Logic</th>
                                    <th className="px-8 py-5 text-[12px] font-bold text-[#05144b] uppercase">Timestamp</th>
                                    <th className="px-8 py-5 text-[12px] font-bold text-[#05144b] uppercase text-right">Ops</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                                {filtered.map(b => (
                                    <tr key={b._id} className="group hover:bg-gray-50/50 transition-all font-bold">
                                        <td className="px-8 py-7">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-md bg-[#05144b] text-white flex items-center justify-center font-bold text-lg shadow-xl">
                                                    {b.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-black text-[15px]">{b.name}</h4>
                                                    <div className="text-[12px] font-medium text-black/60 mt-1">{b.phone}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div>
                                                <h4 className="font-bold text-black text-[14px] line-clamp-1">{getPropertyName(b)}</h4>
                                                <div className="text-[11px] font-bold text-black/40 mt-1 uppercase tracking-widest">{b.type}</div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div className="text-[13px] font-bold text-black uppercase">
                                                {new Date(b.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <select
                                                 className="bg-gray-100 text-black px-4 py-2 rounded-md font-bold text-[12px] border-none focus:ring-2 focus:ring-[#05144b]"
                                                value={b.status}
                                                onChange={e => handleStatusChange(b._id, e.target.value)}
                                            >
                                                <option value="Pending">Pending</option>
                                                <option value="Completed">Completed</option>
                                                <option value="Cancelled">Cancelled</option>
                                            </select>
                                        </td>
                                        <td className="px-8 py-7 text-right">
                                            <div className="flex items-center justify-end gap-3">
                                                <button 
                                                    className="px-5 py-2.5 rounded bg-white border border-[#05144b] text-[#05144b] text-[11px] font-bold hover:bg-blue-50 transition-all shadow-lg"
                                                    onClick={() => setViewBooking(b)}
                                                >
                                                    View Details
                                                </button>
                                                <button 
                                                    className="w-9 h-9 rounded bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                    onClick={() => setDeleteId(b._id)}
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Modals */}
            {viewBooking && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center px-4 overflow-hidden">
                     <div className="absolute inset-0 bg-[#05144b]/40 backdrop-blur-sm" onClick={() => setViewBooking(null)}></div>
                    <div className="relative w-full max-w-4xl bg-white rounded-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-8 bg-[#05144b] text-white flex justify-between items-center shrink-0">
                            <div>
                                <h2 className="text-2xl font-bold tracking-tight">Inquiry Profile</h2>
                                <p className="text-white/60 text-[12px] font-bold mt-1 uppercase tracking-widest">ID: {viewBooking._id}</p>
                            </div>
                            <button onClick={() => setViewBooking(null)} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all">
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>
                        <div className="p-10 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                                <div className="space-y-8">
                                    <div>
                                        <p className="text-[11px] font-bold text-black/40 uppercase mb-3 tracking-widest">Target Interest</p>
                                        <h4 className="text-xl font-bold text-black">{getPropertyName(viewBooking)}</h4>
                                        <p className="text-sm font-bold text-black/60 mt-2">{getPropertyLocation(viewBooking)}</p>
                                    </div>
                                    <div className="pt-6 border-t border-gray-100">
                                        <p className="text-[11px] font-bold text-black/40 uppercase mb-3 tracking-widest">Timeline</p>
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <span className="text-[12px] font-bold text-black/40">CAPTURED</span>
                                                <span className="text-[13px] font-bold text-black">{new Date(viewBooking.createdAt).toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-[12px] font-bold text-black/40">SOURCE TYPE</span>
                                                <span className="text-[13px] font-bold text-black uppercase tracking-widest">{viewBooking.type}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-8 border-x border-gray-100 px-10">
                                    <div>
                                        <p className="text-[11px] font-bold text-black/40 uppercase mb-3 tracking-widest">Requirements</p>
                                        <div className="space-y-4">
                                            <div className="p-4 rounded bg-gray-50 border border-gray-100">
                                                <p className="text-[10px] font-bold text-black/40 uppercase mb-1">Purpose</p>
                                                <p className="text-[14px] font-bold text-black uppercase">{viewBooking.purpose || 'Buy'}</p>
                                            </div>
                                            <div className="p-4 rounded bg-gray-50 border border-gray-100">
                                                <p className="text-[10px] font-bold text-black/40 uppercase mb-1">Property Type</p>
                                                <p className="text-[14px] font-bold text-black uppercase">{viewBooking.propertyType || 'Any'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-8">
                                    <div>
                                        <p className="text-[11px] font-bold text-black/40 uppercase mb-3 tracking-widest">Contact Details</p>
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-[10px] font-bold text-black/40 uppercase mb-1">Email</p>
                                                <p className="text-[15px] font-bold text-black">{viewBooking.email || 'N/A'}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-bold text-black/40 uppercase mb-1">Phone</p>
                                                <p className="text-[15px] font-bold text-black">{viewBooking.phone}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pt-6 border-t border-gray-100 flex gap-4">
                                        <a href={`tel:${viewBooking.phone}`} className="flex-1 bg-[#05144b] text-white py-3 rounded font-bold text-[12px] flex items-center justify-center gap-2">
                                            <i className="fa-solid fa-phone"></i> Call
                                        </a>
                                        <a href={`https://wa.me/91${viewBooking.phone}`} target="_blank" className="flex-1 bg-emerald-600 text-white py-3 rounded font-bold text-[12px] flex items-center justify-center gap-2">
                                            <i className="fa-brands fa-whatsapp"></i> WA
                                        </a>
                                    </div>
                                </div>
                            </div>
                            {viewBooking.message && (
                                <div className="mt-10 pt-10 border-t border-gray-100">
                                    <p className="text-[11px] font-bold text-black/40 uppercase mb-4 tracking-widest">Message Narrative</p>
                                    <div className="p-6 bg-gray-50 rounded border border-gray-100 text-black font-bold leading-relaxed">
                                        "{viewBooking.message}"
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {deleteId && (
                <div className="fixed inset-0 z-[1200] flex items-center justify-center px-4">
                     <div className="absolute inset-0 bg-[#05144b]/40 backdrop-blur-sm" onClick={() => setDeleteId(null)}></div>
                    <div className="relative w-full max-w-md bg-white rounded-md shadow-2xl overflow-hidden p-8 text-center">
                        <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6 text-rose-500">
                            <i className="fa-solid fa-triangle-exclamation text-2xl"></i>
                        </div>
                        <h1 className="text-3xl font-bold text-[#05144b] mb-2 leading-none">Inquiry Stream</h1>
                        <p className="text-[#05144b] font-semibold text-[14px]">Direct interest capture from property detail pages and landing zones.</p>
                        <div className="flex gap-4">
                            <button onClick={() => setDeleteId(null)} className="flex-1 py-3 rounded bg-gray-100 text-black font-bold text-[12px]">Cancel</button>
                            <button onClick={handleDelete} className="flex-1 py-3 rounded bg-rose-600 text-white font-bold text-[12px]">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}
